import { Component, input, output, computed } from '@angular/core';

import { PaginationResult } from '../../../services/shared/table-pagination/table-pagination.service';

export interface PageChangeEvent {
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-table-pagination',
  standalone: true,
  imports: [],
  template: `
    <div class="row pagination-row">
      <div class="col-sm-6">
        <div class="dataTables_info" id="DataTables_Table_0_info" role="status" aria-live="polite">
          {{ paginationInfo() }}
        </div>
      </div>
      <div class="col-sm-6">
        <div class="dataTables_paginate paging_simple_numbers" id="DataTables_Table_0_paginate">
          <ul class="pagination">
            <!-- First Page Button -->
            <li class="paginate_button first" 
                [class.disabled]="!paginationData().hasPreviousPage">
              <a href="#" 
                 (click)="goToPage($event, 1)"
                 [attr.aria-disabled]="!paginationData().hasPreviousPage"
                 title="First page">
                &laquo;
              </a>
            </li>
            
            <!-- Previous Button -->
            <li class="paginate_button previous" 
                [class.disabled]="!paginationData().hasPreviousPage">
              <a href="#" 
                 (click)="goToPage($event, paginationData().currentPage - 1)"
                 [attr.aria-disabled]="!paginationData().hasPreviousPage"
                 title="Previous page">
                &lsaquo;
              </a>
            </li>
            
            <!-- Page Numbers -->
            @for (pageNum of pageNumbers(); track pageNum) {
              <li class="paginate_button" 
                  [class.active]="pageNum === paginationData().currentPage">
                <a href="#" 
                   [attr.data-page]="pageNum"
                   (click)="goToPage($event, pageNum)"
                   [attr.aria-current]="pageNum === paginationData().currentPage ? 'page' : null">
                  {{ pageNum }}
                </a>
              </li>
            }
            
            <!-- Next Button -->
            <li class="paginate_button next" 
                [class.disabled]="!paginationData().hasNextPage">
              <a href="#" 
                 (click)="goToPage($event, paginationData().currentPage + 1)"
                 [attr.aria-disabled]="!paginationData().hasNextPage"
                 title="Next page">
                &rsaquo;
              </a>
            </li>
            
            <!-- Last Page Button -->
            <li class="paginate_button last" 
                [class.disabled]="!paginationData().hasNextPage">
              <a href="#" 
                 (click)="goToPage($event, paginationData().totalPages)"
                 [attr.aria-disabled]="!paginationData().hasNextPage"
                 title="Last page">
                &raquo;
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .pagination-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 15px;
      margin-bottom: 15px;
      flex-wrap: nowrap;
      min-height: 40px;
    }
    
    .pagination-row .col-sm-6:first-child {
      flex: 0 0 auto;
      min-width: 0;
    }

    .pagination-row .col-sm-6:last-child {
      flex: 0 0 auto;
      margin-left: auto;
      min-width: 0;
    }
    
    .dataTables_info {
      padding-top: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .dataTables_paginate {
      text-align: right;
      white-space: nowrap;
    }
    
    .pagination {
      margin: 0;
      padding: 0;
      display: flex;
      flex-wrap: nowrap;
      white-space: nowrap;
    }
    
    .paginate_button {
      cursor: pointer;
      flex-shrink: 0;
    }
    
    .paginate_button.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
    
    .paginate_button.disabled a {
      color: #777;
      cursor: not-allowed;
      pointer-events: none;
    }
    
    .paginate_button.active a {
      background-color: #1ab394;
      border-color: #1ab394;
      color: white;
    }
    
    @media (max-width: 768px) {
      .pagination-row {
        flex-direction: column;
        align-items: flex-start;
        flex-wrap: wrap;
      }
      
      .pagination-row .col-sm-6 {
        width: 100%;
        margin-left: 0 !important;
      }
      
      .dataTables_info {
        margin-bottom: 10px;
      }
      
      .dataTables_paginate {
        text-align: center;
      }
    }
  `]
})
export class TablePaginationComponent {
  // Input signals
  paginationData = input.required<PaginationResult<any>>();
  maxPagesToShow = input<number>(5);
  itemDescriptor = input<string>('Entries'); // New input for custom descriptor

  // Output signals
  pageChange = output<PageChangeEvent>();

  // Computed properties
  paginationInfo = computed(() => {
    const data = this.paginationData();
    const descriptor = this.itemDescriptor();
    if (data.totalItems === 0) {
      return `Showing 0 to 0 of 0 ${descriptor} `;
    }
    return `Showing ${data.startIndex} to ${data.endIndex} of ${data.totalItems} ${descriptor} `;
  });

  pageNumbers = computed(() => {
    const data = this.paginationData();
    const maxPages = this.maxPagesToShow();

    if (data.totalPages <= maxPages) {
      return Array.from({ length: data.totalPages }, (_, i) => i + 1);
    }

    const halfPages = Math.floor(maxPages / 2);
    let startPage = Math.max(1, data.currentPage - halfPages);
    let endPage = Math.min(data.totalPages, startPage + maxPages - 1);

    // Adjust if we're near the end
    if (endPage - startPage + 1 < maxPages) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  });

  goToPage(event: Event, page: number): void {
    event.preventDefault();

    const data = this.paginationData();

    // Validate page bounds
    if (page < 1 || page > data.totalPages) {
      return;
    }

    // Don't emit if we're already on the requested page
    if (page === data.currentPage) {
      return;
    }

    this.pageChange.emit({
      page,
      pageSize: data.pageSize
    });
  }
}
